import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

// Handles drag-and-drop reordering of blocks in the compose editor timeline.
// Sends PATCH to reorder_block endpoint with from/to indices.
export default class extends Controller {
  static targets = ["list", "status"]
  static values = { baseUrl: String }

  connect() {
    if (!this.hasListTarget) return

    this.sortable = Sortable.create(this.listTarget, {
      handle: "[data-drag-handle]",
      animation: 150,
      ghostClass: "opacity-30",
      chosenClass: "ring-2 ring-blue-400",
      dragClass: "shadow-lg",
      group: { name: "blocks", pull: false, put: ["palette"] },
      onEnd: this.handleEnd.bind(this)
    })
  }

  disconnect() {
    this.sortable?.destroy()
  }

  async handleEnd(event) {
    const { oldIndex, newIndex, from, to } = event

    // If item came from palette, handle as new block insertion
    if (from !== to) {
      const blockType = event.item.dataset.blockType
      if (blockType) {
        event.item.remove()
        await this.addBlockAtIndex(blockType, newIndex)
      }
      return
    }

    // Same-container reorder
    if (oldIndex === newIndex) return

    this.updateStatus("Saving...")

    try {
      const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
      const url = `${this.baseUrlValue}/reorder_block`

      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "Accept": "text/vnd.turbo-stream.html, text/html"
        },
        body: JSON.stringify({ from_index: oldIndex, to_index: newIndex })
      })

      if (response.ok) {
        this.updateStatus("")
        window.location.href = `${this.baseUrlValue}?block_index=${newIndex}`
      } else {
        this.revertOrder(event)
        this.updateStatus("Reorder failed")
      }
    } catch (error) {
      console.error("Block reorder failed:", error)
      this.revertOrder(event)
      this.updateStatus("Reorder failed")
    }
  }

  async addBlockAtIndex(blockType, atIndex) {
    try {
      const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
      const url = `${this.baseUrlValue}/add_block`

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "Accept": "text/vnd.turbo-stream.html, text/html"
        },
        body: JSON.stringify({ block_type: blockType, at_index: atIndex })
      })

      if (response.ok) {
        window.location.href = `${this.baseUrlValue}?block_index=${atIndex}`
      }
    } catch (error) {
      console.error("Add block failed:", error)
    }
  }

  revertOrder(event) {
    const { oldIndex, newIndex } = event
    const parent = this.listTarget
    const children = Array.from(parent.children)
    const movedEl = children[newIndex]

    parent.removeChild(movedEl)

    if (oldIndex >= parent.children.length) {
      parent.appendChild(movedEl)
    } else {
      parent.insertBefore(movedEl, parent.children[oldIndex])
    }
  }

  updateStatus(text) {
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = text
    }
  }
}
